import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Settings,
  Shield,
  Mail,
  Database,
  Users,
  GraduationCap,
  BookOpen,
  Layers,
  Calendar,
  FileText,
  AlertTriangle,
  Key,
  HardDrive,
  Table2,
  CheckCircle2,
  XCircle,
  Trash2,
  Loader2,
  Send,
  Server,
  Lock,
  Clock,
  Save,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { EntityPageHeader } from '@/shared/components'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useDatabaseStats, useFlywayMigrations, useCleanPasswordTokens, useCleanErrorLogs } from '../hooks/use-database-admin'
import { useSecurityStats, useRecentLogins, useFailedLoginsSummary, useUsersMustChangePassword } from '../hooks/use-security-admin'
import { useEmailStats, useSmtpConfig, useRecentEmails, useSendTestEmail, useEmailTemplates, useUpdateEmailTemplate } from '../hooks/use-email-admin'

type Tab = 'security' | 'emails' | 'database'

export function AdminSystemPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<Tab>('database')
  const [daysThreshold, setDaysThreshold] = useState(30)
  const [showTokenConfirm, setShowTokenConfirm] = useState(false)
  const [showErrorConfirm, setShowErrorConfirm] = useState(false)
  const [migrationPage, setMigrationPage] = useState(0)
  const migrationPageSize = 10

  const { data: stats, isLoading: statsLoading } = useDatabaseStats()
  const { data: migrations, isLoading: migrationsLoading } = useFlywayMigrations()
  const cleanTokens = useCleanPasswordTokens()
  const cleanErrors = useCleanErrorLogs()

  const { data: secStats, isLoading: secStatsLoading } = useSecurityStats()
  const { data: recentLogins, isLoading: loginsLoading } = useRecentLogins(20)
  const { data: failedSummary } = useFailedLoginsSummary()
  const { data: usersMustChange } = useUsersMustChangePassword()

  const { data: emailStats, isLoading: emailStatsLoading } = useEmailStats()
  const { data: smtpConfig, isLoading: smtpLoading } = useSmtpConfig()
  const { data: recentEmails, isLoading: recentEmailsLoading } = useRecentEmails(20)
  const sendTestEmail = useSendTestEmail()
  const { data: templates, isLoading: templatesLoading } = useEmailTemplates()
  const updateTemplate = useUpdateEmailTemplate()
  const [activeTemplate, setActiveTemplate] = useState<'CREDENTIALS' | 'PASSWORD_RESET'>('CREDENTIALS')
  const [editSubject, setEditSubject] = useState('')
  const [editBody, setEditBody] = useState('')
  const [templateInitialized, setTemplateInitialized] = useState(false)

  const tabs: { key: Tab; label: string; icon: typeof Settings; disabled: boolean }[] = [
    { key: 'database', label: t('admin.system.tabs.database', 'Base de données'), icon: Database, disabled: false },
    { key: 'security', label: t('admin.system.tabs.security', 'Sécurité'), icon: Shield, disabled: false },
    { key: 'emails', label: t('admin.system.tabs.emails', 'Emails'), icon: Mail, disabled: false },
  ]

  const kpis = stats
    ? [
        { icon: Users, value: stats.totalUsers, label: t('admin.system.database.stats.users', 'Utilisateurs'), bgColor: 'bg-primary/10', iconColor: 'text-primary' },
        { icon: Users, value: stats.totalFormateurs, label: t('admin.system.database.stats.formateurs', 'Formateurs'), bgColor: 'bg-blue-50', iconColor: 'text-blue-600' },
        { icon: GraduationCap, value: stats.totalEtudiants, label: t('admin.system.database.stats.etudiants', 'Étudiants'), bgColor: 'bg-emerald-50', iconColor: 'text-emerald-600' },
        { icon: BookOpen, value: stats.totalProgrammes, label: t('admin.system.database.stats.programmes', 'Programmes'), bgColor: 'bg-violet-50', iconColor: 'text-violet-600' },
        { icon: Layers, value: stats.totalMatieres, label: t('admin.system.database.stats.matieres', 'Matières'), bgColor: 'bg-amber-50', iconColor: 'text-amber-600' },
        { icon: Settings, value: stats.totalActions, label: t('admin.system.database.stats.actions', 'Actions'), bgColor: 'bg-rose-50', iconColor: 'text-rose-600' },
        { icon: Table2, value: stats.totalTypesActivite, label: t('admin.system.database.stats.typesActivite', "Types d'activité"), bgColor: 'bg-cyan-50', iconColor: 'text-cyan-600' },
        { icon: Calendar, value: stats.totalPeriodes, label: t('admin.system.database.stats.periodes', 'Périodes'), bgColor: 'bg-teal-50', iconColor: 'text-teal-600' },
        { icon: FileText, value: stats.totalAuditLogs, label: t('admin.system.database.stats.auditLogs', "Logs d'audit"), bgColor: 'bg-indigo-50', iconColor: 'text-indigo-600' },
        { icon: AlertTriangle, value: stats.totalErrorLogs, label: t('admin.system.database.stats.errorLogs', "Logs d'erreurs"), bgColor: 'bg-red-50', iconColor: 'text-red-600' },
        { icon: Key, value: stats.activePasswordResetTokens, label: t('admin.system.database.stats.activeTokens', 'Tokens actifs'), bgColor: 'bg-orange-50', iconColor: 'text-orange-600' },
        { icon: HardDrive, value: stats.databaseSize, label: t('admin.system.database.stats.databaseSize', 'Taille de la base'), bgColor: 'bg-neutral-100', iconColor: 'text-neutral-600' },
      ]
    : []

  return (
    <div className="space-y-6">
      <EntityPageHeader
        icon={Settings}
        title={t('admin.system.title', 'Système')}
        subtitle={t('admin.system.subtitle', "Configuration et maintenance de l'application")}
      />

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-neutral-200 bg-neutral-50 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => !tab.disabled && setActiveTab(tab.key)}
            disabled={tab.disabled}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-white text-neutral-900 shadow-sm'
                : tab.disabled
                  ? 'cursor-not-allowed text-neutral-300'
                  : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
            {tab.disabled && (
              <span className="text-[10px] uppercase tracking-wider text-neutral-300">
                {t('admin.system.comingSoon', 'Bientôt disponible')}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Database Tab Content */}
      {activeTab === 'database' && (
        <div className="space-y-6">
          {/* Stats Section */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.database.stats.sectionTitle', 'Statistiques')}
            </h2>

            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  {kpis.map((kpi) => (
                    <div
                      key={kpi.label}
                      className="flex items-center gap-2.5 rounded-lg border border-neutral-200 bg-white px-3 py-2.5"
                    >
                      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${kpi.bgColor}`}>
                        <kpi.icon className={`h-3.5 w-3.5 ${kpi.iconColor}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-neutral-900">{kpi.value}</p>
                        <p className="truncate text-[10px] text-neutral-500">{kpi.label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Users by role breakdown */}
                {stats && Object.keys(stats.usersByRole).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.usersByRole).map(([role, count]) => (
                      <span
                        key={role}
                        className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-600"
                      >
                        <span className="font-medium">
                          {t(`admin.system.database.stats.role.${role}`, role)}
                        </span>
                        <span className="text-neutral-400">·</span>
                        <span className="font-semibold text-neutral-900">{count}</span>
                      </span>
                    ))}
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-600">
                      <span className="font-medium">
                        {t('admin.system.database.stats.tables', 'Tables')}
                      </span>
                      <span className="text-neutral-400">·</span>
                      <span className="font-semibold text-neutral-900">{stats.totalTableCount}</span>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Migrations Section */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.database.migrations.sectionTitle', 'Historique des migrations Flyway')}
            </h2>

            {migrationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : !migrations || migrations.length === 0 ? (
              <div className="rounded-xl border border-neutral-200 bg-white px-6 py-8 text-center text-sm text-neutral-400">
                {t('admin.system.database.migrations.empty', 'Aucune migration trouvée')}
              </div>
            ) : (
              (() => {
                const totalPages = Math.ceil(migrations.length / migrationPageSize)
                const paginated = migrations.slice(
                  migrationPage * migrationPageSize,
                  (migrationPage + 1) * migrationPageSize,
                )
                return (
                  <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-neutral-200 bg-neutral-50/50">
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                              {t('admin.system.database.migrations.col.rank', 'Rang')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                              {t('admin.system.database.migrations.col.version', 'Version')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                              {t('admin.system.database.migrations.col.description', 'Description')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                              {t('admin.system.database.migrations.col.type', 'Type')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                              {t('admin.system.database.migrations.col.installedOn', "Date d'installation")}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                              {t('admin.system.database.migrations.col.executionTime', 'Durée')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                              {t('admin.system.database.migrations.col.status', 'Statut')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                          {paginated.map((m) => (
                            <tr key={m.installedRank} className="hover:bg-neutral-50/50">
                              <td className="px-4 py-3 text-sm font-mono text-neutral-500">
                                {m.installedRank}
                              </td>
                              <td className="px-4 py-3 text-sm font-mono font-medium text-neutral-900">
                                {m.version ?? '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-neutral-700">
                                {m.description}
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center rounded bg-neutral-100 px-1.5 py-0.5 text-xs font-medium text-neutral-600">
                                  {m.type}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-neutral-500">
                                {new Date(m.installedOn).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </td>
                              <td className="px-4 py-3 text-sm font-mono text-neutral-500">
                                {m.executionTime} ms
                              </td>
                              <td className="px-4 py-3">
                                {m.success ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                                    <CheckCircle2 className="h-3 w-3" />
                                    {t('admin.system.database.migrations.success', 'Succès')}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                                    <XCircle className="h-3 w-3" />
                                    {t('admin.system.database.migrations.failed', 'Échec')}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3">
                        <span className="text-xs text-neutral-500">
                          {migrationPage * migrationPageSize + 1}–{Math.min((migrationPage + 1) * migrationPageSize, migrations.length)} sur {migrations.length}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setMigrationPage((p) => Math.max(0, p - 1))}
                            disabled={migrationPage === 0}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => (
                            <button
                              key={i}
                              onClick={() => setMigrationPage(i)}
                              className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-colors ${
                                migrationPage === i
                                  ? 'bg-primary text-white'
                                  : 'border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                          <button
                            onClick={() => setMigrationPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={migrationPage === totalPages - 1}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()
            )}
          </div>

          {/* Maintenance Section */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.database.maintenance.sectionTitle', 'Maintenance')}
            </h2>

            <div className="space-y-4">
              {/* Clean tokens */}
              <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-neutral-900">
                      {t('admin.system.database.maintenance.cleanTokens', 'Nettoyer les tokens')}
                    </h3>
                    <p className="mt-1 text-xs text-neutral-500">
                      {t('admin.system.database.maintenance.cleanTokensDesc', 'Supprime les tokens de réinitialisation de mot de passe expirés ou utilisés')}
                    </p>
                    {stats && (
                      <div className="mt-2 flex gap-3 text-xs text-neutral-400">
                        <span>{t('admin.system.database.stats.expiredTokens', 'Tokens expirés')}: <strong className="text-neutral-600">{stats.expiredPasswordResetTokens}</strong></span>
                        <span>{t('admin.system.database.stats.usedTokens', 'Tokens utilisés')}: <strong className="text-neutral-600">{stats.usedPasswordResetTokens}</strong></span>
                      </div>
                    )}
                    {cleanTokens.isSuccess && (
                      <p className="mt-2 text-xs font-medium text-emerald-600">
                        {t('admin.system.database.maintenance.resultTokens', '{{count}} token(s) supprimé(s)', { count: cleanTokens.data.deletedCount })}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {showTokenConfirm ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-neutral-500">
                          {t('admin.system.database.maintenance.confirmTokens', 'Voulez-vous supprimer les tokens expirés et utilisés ?')}
                        </span>
                        <button
                          onClick={() => {
                            cleanTokens.mutate()
                            setShowTokenConfirm(false)
                          }}
                          disabled={cleanTokens.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                        >
                          {cleanTokens.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                          {t('admin.system.database.maintenance.confirm', 'Confirmer')}
                        </button>
                        <button
                          onClick={() => setShowTokenConfirm(false)}
                          className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
                        >
                          {t('admin.system.database.maintenance.cancel', 'Annuler')}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowTokenConfirm(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                        {t('admin.system.database.maintenance.cleanTokens', 'Nettoyer les tokens')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Clean error logs */}
              <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-neutral-900">
                      {t('admin.system.database.maintenance.cleanErrors', 'Nettoyer les erreurs')}
                    </h3>
                    <p className="mt-1 text-xs text-neutral-500">
                      {t('admin.system.database.maintenance.cleanErrorsDesc', "Supprime les logs d'erreurs plus anciens que le seuil spécifié")}
                    </p>
                    {stats && (
                      <p className="mt-2 text-xs text-neutral-400">
                        {t('admin.system.database.stats.errorLogs', "Logs d'erreurs")}: <strong className="text-neutral-600">{stats.totalErrorLogs}</strong>
                      </p>
                    )}
                    {cleanErrors.isSuccess && (
                      <p className="mt-2 text-xs font-medium text-emerald-600">
                        {t('admin.system.database.maintenance.resultErrors', '{{count}} erreur(s) supprimée(s)', { count: cleanErrors.data.deletedCount })}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {showErrorConfirm ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-neutral-500">
                          {t('admin.system.database.maintenance.confirmErrors', 'Voulez-vous supprimer les erreurs de plus de {{days}} jours ?', { days: daysThreshold })}
                        </span>
                        <button
                          onClick={() => {
                            cleanErrors.mutate(daysThreshold)
                            setShowErrorConfirm(false)
                          }}
                          disabled={cleanErrors.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                        >
                          {cleanErrors.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                          {t('admin.system.database.maintenance.confirm', 'Confirmer')}
                        </button>
                        <button
                          onClick={() => setShowErrorConfirm(false)}
                          className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
                        >
                          {t('admin.system.database.maintenance.cancel', 'Annuler')}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-neutral-500">
                          {t('admin.system.database.maintenance.daysThreshold', 'Seuil (jours)')}
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={365}
                          value={daysThreshold}
                          onChange={(e) => setDaysThreshold(Math.max(1, parseInt(e.target.value) || 30))}
                          className="h-8 w-16 rounded-lg border border-neutral-300 bg-neutral-50 px-2 text-center text-sm text-neutral-700 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
                        />
                        <button
                          onClick={() => setShowErrorConfirm(true)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3 w-3" />
                          {t('admin.system.database.maintenance.cleanErrors', 'Nettoyer les erreurs')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab Content */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* Security KPIs */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.security.stats.sectionTitle', "Vue d'ensemble")}
            </h2>
            {secStatsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : secStats && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { icon: CheckCircle2, value: secStats.loginsToday, label: t('admin.system.security.stats.loginsToday', "Connexions aujourd'hui"), bgColor: 'bg-emerald-50', iconColor: 'text-emerald-600' },
                  { icon: XCircle, value: secStats.failedLoginsToday, label: t('admin.system.security.stats.failedToday', "Échecs aujourd'hui"), bgColor: 'bg-red-50', iconColor: 'text-red-600' },
                  { icon: AlertTriangle, value: secStats.failedLoginsLast7Days, label: t('admin.system.security.stats.failed7Days', 'Échecs (7 jours)'), bgColor: 'bg-orange-50', iconColor: 'text-orange-600' },
                  { icon: Key, value: secStats.usersRequiringPasswordChange, label: t('admin.system.security.stats.mustChangePassword', 'Mdp à changer'), bgColor: 'bg-amber-50', iconColor: 'text-amber-600' },
                ].map((kpi) => (
                  <div key={kpi.label} className="flex items-center gap-2.5 rounded-lg border border-neutral-200 bg-white px-3 py-2.5">
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${kpi.bgColor}`}>
                      <kpi.icon className={`h-3.5 w-3.5 ${kpi.iconColor}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-neutral-900">{kpi.value}</p>
                      <p className="truncate text-[10px] text-neutral-500">{kpi.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Logins */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.security.recentLogins.sectionTitle', 'Connexions récentes')}
            </h2>
            {loginsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : !recentLogins || recentLogins.length === 0 ? (
              <div className="rounded-xl border border-neutral-200 bg-white px-6 py-8 text-center text-sm text-neutral-400">
                {t('admin.system.security.recentLogins.empty', 'Aucune connexion récente')}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200 bg-neutral-50/50">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.recentLogins.col.date', 'Date')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.recentLogins.col.email', 'Email')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.recentLogins.col.action', 'Statut')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.recentLogins.col.ip', 'Adresse IP')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {recentLogins.map((log) => (
                        <tr key={log.id} className="hover:bg-neutral-50/50">
                          <td className="px-4 py-2.5 text-sm text-neutral-500">
                            {new Date(log.createdAt).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-2.5 text-sm font-medium text-neutral-900">
                            {log.userEmail ?? '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              log.action === 'LOGIN'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-red-50 text-red-700'
                            }`}>
                              {t(`admin.system.security.recentLogins.${log.action}`, log.action)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-sm font-mono text-neutral-500">
                            {log.ipAddress ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Failed Logins Summary */}
          {failedSummary && failedSummary.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-neutral-900">
                {t('admin.system.security.failedSummary.sectionTitle', 'Tentatives échouées par utilisateur')}
              </h2>
              <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200 bg-neutral-50/50">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.failedSummary.col.email', 'Email')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.failedSummary.col.count', 'Nb échecs')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.failedSummary.col.lastAttempt', 'Dernière tentative')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.failedSummary.col.lastIp', 'Dernière IP')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {failedSummary.map((entry) => (
                        <tr key={entry.userEmail} className="hover:bg-neutral-50/50">
                          <td className="px-4 py-2.5 text-sm font-medium text-neutral-900">
                            {entry.userEmail}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-bold text-red-700">
                              {entry.failedCount}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-sm text-neutral-500">
                            {new Date(entry.lastAttempt).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-2.5 text-sm font-mono text-neutral-500">
                            {entry.lastIpAddress ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Password Policy */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.security.passwordPolicy.sectionTitle', 'Politique de mots de passe')}
            </h2>
            <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  t('admin.system.security.passwordPolicy.minLength', 'Minimum 8 caractères'),
                  t('admin.system.security.passwordPolicy.uppercase', 'Au moins 1 majuscule'),
                  t('admin.system.security.passwordPolicy.lowercase', 'Au moins 1 minuscule'),
                  t('admin.system.security.passwordPolicy.digit', 'Au moins 1 chiffre'),
                ].map((rule) => (
                  <div key={rule} className="flex items-center gap-2 text-sm text-neutral-700">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    {rule}
                  </div>
                ))}
              </div>
              {secStats && (
                <div className="mt-4 flex items-center gap-2 border-t border-neutral-100 pt-4 text-sm text-neutral-500">
                  <Key className="h-4 w-4 text-neutral-400" />
                  {t('admin.system.security.passwordPolicy.jwtExpiration', 'Expiration du token JWT')}:
                  <span className="font-semibold text-neutral-900">
                    {Math.round(secStats.jwtExpirationMs / 60000)} min
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Users Must Change Password */}
          {usersMustChange && usersMustChange.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-neutral-900">
                {t('admin.system.security.usersMustChange.sectionTitle', 'Utilisateurs devant changer leur mot de passe')}
              </h2>
              <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200 bg-neutral-50/50">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.usersMustChange.col.name', 'Nom')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.usersMustChange.col.email', 'Email')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.usersMustChange.col.role', 'Rôle')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.security.usersMustChange.col.createdAt', 'Créé le')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {usersMustChange.map((user) => (
                        <tr key={user.id} className="hover:bg-neutral-50/50">
                          <td className="px-4 py-2.5 text-sm font-medium text-neutral-900">
                            {user.prenom} {user.nom}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-neutral-600">
                            {user.email}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="inline-flex items-center rounded bg-neutral-100 px-1.5 py-0.5 text-xs font-medium text-neutral-600">
                              {user.profil}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-sm text-neutral-500">
                            {new Date(user.createdAt).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Emails Tab Content */}
      {activeTab === 'emails' && (
        <div className="space-y-6">
          {/* Email KPIs */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.emails.stats.sectionTitle', "Vue d'ensemble")}
            </h2>
            {emailStatsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : emailStats && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { icon: Send, value: emailStats.emailsSentToday, label: t('admin.system.emails.stats.sentToday', "Envoyés aujourd'hui"), bgColor: 'bg-blue-50', iconColor: 'text-blue-600' },
                  { icon: Mail, value: emailStats.emailsSent7Days, label: t('admin.system.emails.stats.sent7Days', 'Envoyés (7 jours)'), bgColor: 'bg-indigo-50', iconColor: 'text-indigo-600' },
                  { icon: Key, value: emailStats.activeResetTokens, label: t('admin.system.emails.stats.activeTokens', 'Tokens actifs'), bgColor: 'bg-emerald-50', iconColor: 'text-emerald-600' },
                  { icon: Clock, value: emailStats.expiredResetTokens, label: t('admin.system.emails.stats.expiredTokens', 'Tokens expirés'), bgColor: 'bg-orange-50', iconColor: 'text-orange-600' },
                ].map((kpi) => (
                  <div key={kpi.label} className="flex items-center gap-2.5 rounded-lg border border-neutral-200 bg-white px-3 py-2.5">
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${kpi.bgColor}`}>
                      <kpi.icon className={`h-3.5 w-3.5 ${kpi.iconColor}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-neutral-900">{kpi.value}</p>
                      <p className="truncate text-[10px] text-neutral-500">{kpi.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SMTP Configuration + Test */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* SMTP Config */}
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-neutral-900">
                {t('admin.system.emails.smtp.sectionTitle', 'Configuration SMTP')}
              </h2>
              {smtpLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : smtpConfig && (
                <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
                  <div className="space-y-3">
                    {[
                      { icon: Server, label: t('admin.system.emails.smtp.host', 'Serveur'), value: smtpConfig.host },
                      { icon: HardDrive, label: t('admin.system.emails.smtp.port', 'Port'), value: String(smtpConfig.port) },
                      { icon: Lock, label: t('admin.system.emails.smtp.encryption', 'Chiffrement'), value: smtpConfig.encryption },
                      { icon: Mail, label: t('admin.system.emails.smtp.sender', 'Expéditeur'), value: smtpConfig.sender },
                      { icon: Settings, label: t('admin.system.emails.smtp.profile', 'Profil actif'), value: smtpConfig.activeProfile },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-3">
                        <item.icon className="h-4 w-4 shrink-0 text-neutral-400" />
                        <span className="text-xs text-neutral-500">{item.label}</span>
                        <span className="ml-auto text-sm font-medium text-neutral-900">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Send Test Email */}
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-neutral-900">
                {t('admin.system.emails.test.sectionTitle', "Test d'envoi")}
              </h2>
              <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
                <p className="text-xs text-neutral-500">
                  {t('admin.system.emails.test.description', 'Envoyer un email de test pour vérifier la configuration SMTP')}
                </p>
                <button
                  onClick={() => sendTestEmail.mutate(user?.email ?? '')}
                  disabled={sendTestEmail.isPending}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  {sendTestEmail.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {sendTestEmail.isPending
                    ? t('admin.system.emails.test.sending', 'Envoi en cours...')
                    : t('admin.system.emails.test.send', 'Envoyer un email de test')
                  }
                </button>
                {sendTestEmail.isSuccess && (
                  <p className="mt-3 text-xs font-medium text-emerald-600">
                    {t('admin.system.emails.test.success', 'Email de test envoyé avec succès')}
                  </p>
                )}
                {sendTestEmail.isError && (
                  <p className="mt-3 text-xs font-medium text-red-600">
                    {t('admin.system.emails.test.error', "Erreur lors de l'envoi de l'email de test")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Email Templates Editor */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.emails.templates.sectionTitle', 'Templates d\'emails')}
            </h2>
            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : templates && templates.length > 0 ? (
              <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
                <div className="flex border-b border-neutral-200">
                  {[
                    { code: 'CREDENTIALS' as const, icon: Mail, label: t('admin.system.emails.templates.credentials', 'Email de bienvenue') },
                    { code: 'PASSWORD_RESET' as const, icon: Key, label: t('admin.system.emails.templates.reset', 'Email de réinitialisation') },
                  ].map((tab) => (
                    <button
                      key={tab.code}
                      onClick={() => {
                        setActiveTemplate(tab.code)
                        const tpl = templates.find((tp) => tp.code === tab.code)
                        if (tpl) {
                          setEditSubject(tpl.subject)
                          setEditBody(tpl.body)
                        }
                        setTemplateInitialized(true)
                        updateTemplate.reset()
                      }}
                      className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-all ${
                        activeTemplate === tab.code
                          ? 'border-b-2 border-primary text-primary'
                          : 'text-neutral-500 hover:text-neutral-700'
                      }`}
                    >
                      <tab.icon className="h-3.5 w-3.5" />
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="p-5 space-y-4">
                  {(() => {
                    const currentTpl = templates.find((tp) => tp.code === activeTemplate)
                    if (!currentTpl) return null
                    // Initialize edit fields on first render
                    if (!templateInitialized && currentTpl) {
                      setTimeout(() => {
                        setEditSubject(currentTpl.subject)
                        setEditBody(currentTpl.body)
                        setTemplateInitialized(true)
                      }, 0)
                    }
                    const placeholders = activeTemplate === 'CREDENTIALS'
                      ? ['{prenom}', '{nom}', '{login}', '{password}']
                      : ['{prenom}', '{nom}', '{resetUrl}']
                    const hasChanges = editSubject !== currentTpl.subject || editBody !== currentTpl.body
                    return (
                      <>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-neutral-700">
                            {t('admin.system.emails.templates.subject', 'Sujet')}
                          </label>
                          <input
                            type="text"
                            value={editSubject}
                            onChange={(e) => setEditSubject(e.target.value)}
                            className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-neutral-700">
                            {t('admin.system.emails.templates.body', 'Corps du message')}
                          </label>
                          <textarea
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            rows={14}
                            className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-neutral-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                          />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          <span className="text-[10px] text-neutral-400">
                            {t('admin.system.emails.templates.placeholders', 'Variables disponibles :')}
                          </span>
                          {placeholders.map((p) => (
                            <span key={p} className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[10px] text-neutral-600">
                              {p}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateTemplate.mutate({
                              code: activeTemplate,
                              subject: editSubject,
                              body: editBody,
                            })}
                            disabled={updateTemplate.isPending || !hasChanges}
                            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                          >
                            {updateTemplate.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                            {t('admin.system.emails.templates.save', 'Sauvegarder')}
                          </button>
                          {updateTemplate.isSuccess && (
                            <p className="text-xs font-medium text-emerald-600">
                              {t('admin.system.emails.templates.saveSuccess', 'Template sauvegardé')}
                            </p>
                          )}
                          {updateTemplate.isError && (
                            <p className="text-xs font-medium text-red-600">
                              {t('admin.system.emails.templates.saveError', 'Erreur lors de la sauvegarde')}
                            </p>
                          )}
                        </div>
                      </>
                    )
                  })()}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-neutral-200 bg-white px-6 py-8 text-center text-sm text-neutral-400">
                {t('admin.system.emails.templates.empty', 'Aucun template trouvé')}
              </div>
            )}
          </div>

          {/* Recent Emails */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-neutral-900">
              {t('admin.system.emails.recent.sectionTitle', 'Emails récents')}
            </h2>
            {recentEmailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : !recentEmails || recentEmails.length === 0 ? (
              <div className="rounded-xl border border-neutral-200 bg-white px-6 py-8 text-center text-sm text-neutral-400">
                {t('admin.system.emails.recent.empty', 'Aucun email envoyé récemment')}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200 bg-neutral-50/50">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.emails.recent.col.date', 'Date')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.emails.recent.col.type', 'Type')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.emails.recent.col.detail', 'Détail')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          {t('admin.system.emails.recent.col.ip', 'Adresse IP')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {recentEmails.map((log) => (
                        <tr key={log.id} className="hover:bg-neutral-50/50">
                          <td className="px-4 py-2.5 text-sm text-neutral-500">
                            {new Date(log.createdAt).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              log.action === 'EMAIL_CREDENTIALS_SENT'
                                ? 'bg-blue-50 text-blue-700'
                                : log.action === 'EMAIL_RESET_SENT'
                                  ? 'bg-amber-50 text-amber-700'
                                  : 'bg-neutral-100 text-neutral-600'
                            }`}>
                              {t(`admin.system.emails.recent.${log.action}`, log.action)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-sm text-neutral-700">
                            {log.detail ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-sm font-mono text-neutral-500">
                            {log.ipAddress ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
